import { translateLabel } from "@/i18n/messages";
import React from "react";
import { ShareNetworkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { copyShareLink, facebookShareUrl, nativeShare, whatsappShareUrl } from "@/lib/share";
import { trackEvent } from "@/lib/analytics";

const openExternal = (url) => window.open(url, "_blank", "noopener,noreferrer");

export function ShareMenu({ payload, className = "", label = "Share", compact = false }) {
  if (!payload?.url) return null;

  const trackShare = (method) => {
    trackEvent("share", {
      method,
      content_type: payload.analytics?.content_type || "unknown",
      item_id: payload.analytics?.item_id || "",
      item_name: payload.analytics?.item_name || payload.title || "",
    });
  };

  const shareToApps = async () => {
    trackShare("native");
    const shared = await nativeShare(payload);
    if (!shared) {
      const copied = await copyShareLink(payload.url).catch(() => false);
      copied ? toast.success(translateLabel("Link copied")) : toast.error(translateLabel("Sharing is not available on this device"));
    }
  };

  const shareInstagram = async () => {
    trackShare("instagram");
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      toast.info(translateLabel("Choose Instagram"), { description: translateLabel("Select Instagram from the share sheet.") });
    }
    const shared = await nativeShare(payload);
    if (shared) return;
    const copied = await copyShareLink(payload.url).catch(() => false);
    if (copied) toast(translateLabel("Link copied"), { description: translateLabel("Open Instagram and paste it into your story or message.") });
    else toast.error(translateLabel("Instagram sharing is not available in this browser"));
  };

  const shareFacebook = async () => {
    trackShare("facebook");
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      toast.info(translateLabel("Choose Facebook"), { description: translateLabel("Select Facebook from the share sheet.") });
      const shared = await nativeShare(payload);
      if (shared) return;
    }
    openExternal(facebookShareUrl(payload.url));
  };

  const copyLink = async () => {
    trackShare("copy_link");
    const copied = await copyShareLink(payload.url).catch(() => false);
    copied ? toast.success(translateLabel("Link copied")) : toast.error(translateLabel("Could not copy link"));
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="share-trigger"
          className={`flex items-center justify-center gap-2 rounded-full border border-white/15 bg-black/55 font-bold text-white backdrop-blur-md transition-colors hover:bg-black/75 active:scale-95 ${compact ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm sm:px-5"} ${className}`}
        >
          <ShareNetworkIcon size={compact ? 15 : 18} weight="bold" /> {translateLabel(label)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[1400] w-56 border-white/10 bg-[#1c1c1f] text-white">
        <DropdownMenuItem onSelect={shareToApps} className="cursor-pointer focus:bg-white/10">
          {translateLabel("Share to apps…")}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onSelect={() => {
            trackShare("whatsapp");
            openExternal(whatsappShareUrl(payload));
          }}
          className="cursor-pointer focus:bg-white/10"
        >
          WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={shareFacebook}
          className="cursor-pointer focus:bg-white/10"
        >
          Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={shareInstagram} className="cursor-pointer focus:bg-white/10">
          Instagram
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onSelect={copyLink} className="cursor-pointer focus:bg-white/10">
          {translateLabel("Copy link")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
